// Baselayer Results Explorer — reusable empty state for an unordered add-on.
// Renders an EmptyState with an "Order [X]" button that opens an order modal;
// while the add-on's row is Submitted/Processing it shows a loading state
// instead. Liens carries optional states + include-officers options; the rest
// order with no body. The order itself is fired by the parent via useOrderAddOn.

import { useEffect, useState } from "react";
import { Loader2 } from "lucide-react";
import {
  Button,
  EmptyState,
  Input,
  Label,
} from "@/design-system/components/ui";
import { Modal } from "@/components/ui/Modal";
import { useToast } from "@/components/ui/Toast";
import { ApiError } from "@/api/client";
import { useOrderAddOn, type OrderAddOnInput } from "@/hooks/backgroundChecks/useAddOns";
import type {
  BackgroundCheckAddOnDto,
  BackgroundCheckAddOnType,
} from "@/api/backgroundChecks/addOnsApi";

interface AddOnEmptyStateProps {
  checkId: string;
  addOnType: Exclude<BackgroundCheckAddOnType, "DocketDetail">;
  /** Human label, e.g. "Liens", "Litigations". */
  label: string;
  title: string;
  description: string;
  /** The matching add-on row (if any) — drives the in-progress state. */
  addOn: BackgroundCheckAddOnDto | undefined;
}

function isInProgress(addOn: BackgroundCheckAddOnDto | undefined): boolean {
  return addOn?.status === "Submitted" || addOn?.status === "Processing";
}

export function AddOnEmptyState({
  checkId,
  addOnType,
  label,
  title,
  description,
  addOn,
}: AddOnEmptyStateProps) {
  const [modalOpen, setModalOpen] = useState(false);
  const inProgress = isInProgress(addOn);

  if (inProgress) {
    return (
      <EmptyState
        icon={Loader2}
        title={`${label} ordered`}
        description="Baselayer is processing this request. Results appear here once it finishes."
      />
    );
  }

  const failed = addOn?.status === "Failed";

  return (
    <>
      <EmptyState
        title={title}
        description={
          failed
            ? addOn?.failureReason
              ? `The previous order failed: ${addOn.failureReason}. You can re-order below.`
              : "The previous order failed. You can re-order below."
            : description
        }
        action={{ label: `Order ${label}`, onClick: () => setModalOpen(true) }}
      />
      <OrderAddOnModal
        open={modalOpen}
        onClose={() => setModalOpen(false)}
        checkId={checkId}
        addOnType={addOnType}
        label={label}
      />
    </>
  );
}

interface OrderAddOnModalProps {
  open: boolean;
  onClose: () => void;
  checkId: string;
  addOnType: Exclude<BackgroundCheckAddOnType, "DocketDetail">;
  label: string;
}

export function OrderAddOnModal({
  open,
  onClose,
  checkId,
  addOnType,
  label,
}: OrderAddOnModalProps) {
  const { toast } = useToast();
  const order = useOrderAddOn(checkId);
  const [states, setStates] = useState("");
  const [includeOfficers, setIncludeOfficers] = useState(false);

  useEffect(() => {
    if (open) {
      setStates("");
      setIncludeOfficers(false);
    }
  }, [open]);

  const isLiens = addOnType === "Liens";
  const isLitigations = addOnType === "Litigations";

  const handleConfirm = async () => {
    const splitStates = states
      .split(",")
      .map((s) => s.trim())
      .filter(Boolean);

    let input: OrderAddOnInput;
    switch (addOnType) {
      case "Liens":
        input = {
          addOnType: "Liens",
          options: {
            states: splitStates.length ? splitStates : undefined,
            includeOfficers: includeOfficers || undefined,
          },
        };
        break;
      case "Litigations":
        input = {
          addOnType: "Litigations",
          options: { includeOfficers: includeOfficers || undefined },
        };
        break;
      case "Bankruptcies":
        input = { addOnType: "Bankruptcies" };
        break;
      case "WebsiteAnalysis":
        input = { addOnType: "WebsiteAnalysis" };
        break;
      case "ReviewsSocials":
        input = { addOnType: "ReviewsSocials" };
        break;
    }

    try {
      await order.mutateAsync(input);
      toast({ message: `${label} ordered.`, tone: "success" });
      onClose();
    } catch (err) {
      const message =
        err instanceof ApiError
          ? err.errors[0] ?? err.message
          : err instanceof Error
            ? err.message
            : `Could not order ${label}.`;
      toast({ message, tone: "error" });
    }
  };

  return (
    <Modal
      open={open}
      onClose={onClose}
      title={`Order ${label}`}
      description={`Submit a ${label} request to Baselayer for this subject.`}
      size="sm"
      footer={
        <>
          <Button type="button" variant="secondary" onClick={onClose}>
            Cancel
          </Button>
          <Button
            type="button"
            loading={order.isPending}
            disabled={order.isPending}
            onClick={handleConfirm}
            data-testid="order-addon-confirm"
          >
            Order {label}
          </Button>
        </>
      }
    >
      <div className="flex flex-col gap-space-4">
        {isLiens && (
          <div className="flex flex-col gap-space-1">
            <Label htmlFor="order-lien-states">States</Label>
            <Input
              id="order-lien-states"
              value={states}
              onChange={(e) => setStates(e.target.value)}
              placeholder="Comma-separated, e.g. AZ, CA (blank = all)"
            />
          </div>
        )}
        {(isLiens || isLitigations) && (
          <label
            htmlFor="order-include-officers"
            className="flex items-center gap-space-2 text-small text-onyx"
          >
            <input
              id="order-include-officers"
              type="checkbox"
              checked={includeOfficers}
              onChange={(e) => setIncludeOfficers(e.target.checked)}
              className="h-4 w-4 accent-gold"
            />
            Include officers
          </label>
        )}
        {!isLiens && !isLitigations && (
          <p className="text-small text-gray-mid m-0">
            This will submit the request immediately. Results appear here once
            Baselayer finishes processing.
          </p>
        )}
      </div>
    </Modal>
  );
}
